import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Alert, Authentication, Checkout } from '@core/services';
import { NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbModal, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { ForgotModal } from '@shared/components/modals/forgot-modal/forgot-modal.component';
import { touchControls } from '@shared/utils';
import { lastValueFrom } from 'rxjs';
import { RegisterForm } from '../register-form/register-form.component';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { AuthenticateEndpoint } from '@core/endpoints';
import { FEATURE_FLAGS } from '@core/constants';

/**
 * ### **LoginFormComponent**
 *
 * **Selector:** ``<app-login-form>``
 *
 * Un componente es un bloque, que contiene un template, contiene estilos y contiene la lógica, en este caso
 * hace referencia a los elementos que conforman principalmente el incio de sesion, registro de usuarios y perdida de contraseña.
 */
@Component({
  selector: 'app-login-form',
  imports: [
    NgbPopoverModule,
    TranslateModule,
    ReactiveFormsModule,
    RegisterForm,
    LazyLoadImageModule,
  ],
  templateUrl: './login-form.component.html',
  styleUrl: './login-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginForm {
  protected readonly socialLoginEnabled = FEATURE_FLAGS.socialLogin;
  private readonly modalService = inject(NgbModal);
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(Authentication);
  private readonly matDialog = inject(MatDialog);
  private readonly checkoutService = inject(Checkout);
  private readonly alertService = inject(Alert);
  private readonly destroyRef = inject(DestroyRef);

  /** variable de la cual recibe el popover de un componente padre (@Input) */
  readonly popover = input<NgbPopover | null>(null);
  /** Solicita al contenedor actual que cierre el formulario. */
  readonly dismissed = output<void>();
  /** Indica que el formulario se renderiza dentro de una superficie propia, como el bottom sheet móvil. */
  readonly insideSheet = input(false);
  /** Solicita al contenedor móvil que reemplace el login por el registro. */
  readonly registerRequested = output<void>();
  /** Variable que guardara el estado del usuario respecto al logueo, este valor tiene la capacidad de ser emitido a otro componente (@Output) */
  readonly loggedUser = output<boolean>();
  /** Variable que guardara la informacion del formulario de inicio de sesión */
  protected readonly formLogin = signal<FormGroup>(this.fb.group({}));
  /**
   *guarda el estado del formulario de inicio de sesión para saber si falla
   */
  protected readonly loginFail = signal<boolean>(false);

  protected readonly showPassword = signal<boolean>(false);

  /**contiene la informacion de autenticación.*/
  private auth = this.authService.getAuthInformation();

  /**
   * Función encargada de inicializar variables y ejecutar funciones en el momento que el componente es requerido, esto para
   * que a la hora de iniciar la carga ya cuente con valores y se úedan proporcionar las funcionalidades necesarias.
   * @param modalService Parametro que contara con las caracteristicas proporcionadas por NgbModal.
   * @param fb Parametro que hara uso de las funcones de Formbuilder y gestionar formularios.
   * @param authService Parametro que hara uso de las funciones de authService para autenticación.
   * @param matDialog Parametro que contara con las caracteristicas proporcionadas por MatDialog.
   */
  constructor() {
    // dependiendo del estado de autenticación obtiene la informacion de esta misma
    this.authService.onAuthenticate.subscribe(() => {
      this.auth = this.authService.getAuthInformation();
    });
  }

  /**
   * Función nativa de angular que se activa una vez inicia la carga del componente, en este caso ejecuta la función this.createForm().
   */
  ngOnInit() {
    this.createForm();
  }

  /**
   * Función asincrona que es la encargada de validar al usuario y autenticarlo si ha cumplido con los requisitos.
   */
  async authenticate(): Promise<void> {
    const form = this.formLogin();
    console.log({ form });
    if (form.invalid) {
      touchControls(form);
      return;
    }

    const credentials = form.getRawValue();

    let response;

    try {
      response = await lastValueFrom(this.authService.authenticate(credentials));
      console.log({ responseAuth: response });
    } catch (error) {
      console.log({ error });
      this.loginFail.set(true);
      return;
    }

    try {
      response = await lastValueFrom(this.authService.getLoggedUser(response.access));
      console.log({ responseUser: response });
    } catch (error) {
      console.log({ error });
      this.alertService.show({
        isError: true,
        label: 'error_user_information',
      });
      this.loginFail.set(true);
      return;
    }

    // try {
    //   response = await this.checkoutService.setCheckoutStorageFromUser(Number(response.id));
    // } catch (error) {
    //   this.alertService.show({
    //     isError: true,
    //     label: 'error_reservation_cart_list',
    //   });
    //   this.loginFail.set(true);
    //   return;
    // }

    this.dismiss();
    this.loggedUser.emit(true);
    this.loginFail.set(false);
  }

  /** Cierra el popover tradicional o notifica al bottom sheet que debe cerrarse. */
  protected dismiss(): void {
    this.popover()?.close();
    this.dismissed.emit();
  }

  protected togglePasswordVisibility(): void {
    this.showPassword.update((value) => !value);
  }

  /**
   * Función encargada de abrir el modal de registro de usuario.
   * @param content Parametro que recibe el modal que se va a abrir.
   */
  openRegisterForm(content: any): void {
    if (this.insideSheet()) {
      this.registerRequested.emit();
      return;
    }

    this.modalService.open(content, { size: 'lg' });
  }

  /**
   * Función encargada de crear los controls para el formulario de inicio de sesión,
   * los controls capturaran la informacion que sea ingresada en los mismos.
   */
  createForm(): void {
    const form = this.fb.group({
      username: [null, Validators.required],
      password: [null, Validators.required],
    });

    this.formLogin.set(form);

    this.formLogin()
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.loginFail.set(false));
  }

  /**
   * Función encargada de abrir el modal correspondiente a la perdida de contraseña.
   */
  openForgotModal() {
    const dialgoMarkerRef = this.matDialog.open(ForgotModal);
  }

  loginWithProvider(provider: 'facebook' | 'google'): void {
    const urls = {
      facebook: AuthenticateEndpoint.loginFacebook,
      google: AuthenticateEndpoint.loginGoogle,
    };
    window.location.href = urls[provider];
  }
}
